// Dialog per gestione esiti offerte
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertTriangle, FileText, UserCog, ShieldCheck } from 'lucide-react';
import { getEsitoOptionsForOffer, isInClosingPhase } from './offerUtils';

const OfferEsitoDialog = ({ 
  open, 
  offer, 
  onClose, 
  selectedEsito, 
  setSelectedEsito, 
  dataRichiamo, 
  setDataRichiamo, 
  onSave,
  userRole = 'agente',
  currentUserId = null
}) => {
  if (!offer) return null;

  const isOwnOffer = offer.user_id === currentUserId;
  const esitoOptions = getEsitoOptionsForOffer(offer, userRole, isOwnOffer);
  const inClosingPhase = isInClosingPhase(offer);
  const isAdminValutazione = userRole === 'admin' && !isOwnOffer && 
    ['richiesta_documentazione', 'pratica_credito'].includes(offer.esito) &&
    offer.contact_info?.tipo_profilo === 'prospect';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {isAdminValutazione ? 'Valutazione Pratica' : 'Esito Offerta'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-800">
              {offer.contact_info?.nome_cognome}
            </p>
            <p className="text-sm text-slate-500">
              {offer.vehicle_info?.marca} {offer.vehicle_info?.modello}
            </p>
            <div className="flex gap-2 mt-2">
              <Badge className={offer.contact_info?.tipo_profilo === 'prospect' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                {offer.contact_info?.tipo_profilo === 'prospect' ? 'PROSPECT' : 'SMART'}
              </Badge>
              {inClosingPhase && (
                <Badge className="bg-violet-100 text-violet-700">
                  Fase: {offer.esito === 'approvato' ? 'Approvata' : 
                         offer.esito === 'contratto_da_firmare' ? 'Da Firmare' : 
                         offer.esito === 'contratto_firmato' ? 'Firmato' : ''}
                </Badge>
              )}
              {!isOwnOffer && userRole === 'admin' && (
                <Badge variant="outline" className="text-slate-500">
                  Agente: {offer.user_name}
                </Badge>
              )}
            </div>
          </div>
          
          {/* Alert per valutazione Admin */}
          {isAdminValutazione && (
            <Alert className="border-indigo-200 bg-indigo-50">
              <ShieldCheck className="h-4 w-4 text-indigo-600" />
              <AlertDescription className="text-indigo-700">
                <p><strong>Pratica in valutazione</strong> - Puoi approvare o rifiutare questa richiesta.</p>
                <p className="text-sm mt-1">Stato attuale: <strong>{offer.esito === 'richiesta_documentazione' ? 'Richiesta Documentazione' : 'Pratica di Credito'}</strong></p>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Alert blocco Smart -> Richiesta Documentazione */}
          {selectedEsito === 'richiesta_documentazione' && 
           offer.contact_info?.tipo_profilo !== 'prospect' && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Azione non disponibile</AlertTitle>
              <AlertDescription className="text-amber-700">
                <p className="mb-3">
                  Per procedere con l&apos;istruttoria creditizia, è necessario completare l&apos;anagrafica del cliente e portarlo a livello <strong>PROSPECT</strong>.
                </p>
                <Link to={`/dashboard/rubrica?edit=${offer.contact_id}`}>
                  <Button size="sm" variant="outline" className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-100">
                    <UserCog className="w-4 h-4" />
                    Completa Anagrafica
                  </Button>
                </Link>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Info per offerta approvata */}
          {offer.esito === 'approvato' && (
            <Alert className="border-emerald-200 bg-emerald-50">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-700">
                <p><strong>Offerta Approvata</strong> - Seleziona lo stato del contratto.</p>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Info per contratto da firmare */}
          {offer.esito === 'contratto_da_firmare' && (
            <Alert className="border-amber-200 bg-amber-50">
              <FileText className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                <p><strong>Contratto in attesa di firma</strong> - Conferma la firma o riporta ad Approvato.</p>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Info per contratto firmato */}
          {offer.esito === 'contratto_firmato' && (
            <Alert className="border-emerald-200 bg-emerald-50">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-700">
                <p><strong>Contratto Firmato!</strong> L&apos;Admin può ora procedere con l&apos;ORDINE.</p>
                <p className="text-sm mt-1">Se necessario, puoi riportare l&apos;offerta allo stato &quot;Approvato&quot;.</p>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label>
              {isAdminValutazione 
                ? 'Decisione' 
                : offer.esito === 'contratto_firmato' 
                  ? 'Azione disponibile' 
                  : 'Seleziona Esito'}
            </Label>
            <Select value={selectedEsito} onValueChange={setSelectedEsito}>
              <SelectTrigger>
                <SelectValue placeholder={
                  isAdminValutazione
                    ? "Seleziona decisione..."
                    : offer.esito === 'contratto_firmato' 
                      ? "Seleziona per tornare indietro..." 
                      : "Seleziona esito..."
                } />
              </SelectTrigger>
              <SelectContent>
                {esitoOptions.map(opt => {
                  const isBlockedForSmart = 
                    (opt.value === 'richiesta_documentazione' || opt.value === 'pratica_credito') &&
                    offer.contact_info?.tipo_profilo !== 'prospect';
                  
                  const isRollback = opt.value === 'approvato' && inClosingPhase;
                  const isApproval = opt.value === 'approvato' && isAdminValutazione;
                  const isRejection = opt.value === 'rifiutato';
                  
                  return (
                    <SelectItem 
                      key={opt.value} 
                      value={opt.value}
                      disabled={isBlockedForSmart}
                      className={isBlockedForSmart ? 'opacity-50' : ''}
                    >
                      <div className="flex items-center gap-2">
                        <opt.icon className={`w-4 h-4 ${
                          isRollback ? 'text-amber-600' : 
                          isApproval ? 'text-emerald-600' : 
                          isRejection ? 'text-red-600' : ''
                        }`} />
                        <span className={
                          isRollback ? 'text-amber-700' : 
                          isApproval ? 'text-emerald-700 font-medium' : 
                          isRejection ? 'text-red-700 font-medium' : ''
                        }>{opt.label}</span>
                        {isBlockedForSmart && <span className="text-xs text-slate-400 ml-1">(Solo Prospect)</span>}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          
          {selectedEsito === 'richiamare' && (
            <div className="space-y-2">
              <Label>Data Richiamo</Label>
              <Input
                type="date"
                value={dataRichiamo}
                onChange={(e) => setDataRichiamo(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => onClose(false)}
          >
            Annulla
          </Button>
          <Button 
            className={`flex-1 ${
              selectedEsito === 'approvato' ? 'bg-emerald-600 hover:bg-emerald-700' :
              selectedEsito === 'rifiutato' ? 'bg-red-600 hover:bg-red-700' : ''
            }`}
            onClick={onSave}
            disabled={
              (selectedEsito === 'richiesta_documentazione' || selectedEsito === 'pratica_credito') && 
              offer?.contact_info?.tipo_profilo !== 'prospect'
            }
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {isAdminValutazione ? 'Conferma Decisione' : 'Salva Esito'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OfferEsitoDialog;
